import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { PortalAuthProvider, usePortalAuthContext } from '@/contexts/PortalAuthContext';
import { ReadOnlyProvider } from '@/contexts/ReadOnlyContext';

interface PortalProtectedRouteProps {
  children: ReactNode;
}

const PortalProtectedRouteInner = ({ children }: PortalProtectedRouteProps) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isPortalUser, isLoading: portalLoading, isDeactivated } = usePortalAuthContext();
  const location = useLocation();

  // Show loading while checking auth
  if (authLoading || portalLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to portal auth if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/portal/auth" replace state={{ from: location }} />;
  }

  // Redirect to portal auth with error if authenticated but not a portal user
  if (!isPortalUser) {
    return <Navigate to="/portal/auth" replace state={{ accessDenied: true }} />;
  }

  // Wrap with ReadOnlyProvider - deactivated accounts get read-only access
  return (
    <ReadOnlyProvider isDeactivated={isDeactivated} accountType="portal">
      {children}
    </ReadOnlyProvider>
  );
};

const PortalProtectedRoute = ({ children }: PortalProtectedRouteProps) => {
  return (
    <PortalAuthProvider>
      <PortalProtectedRouteInner>{children}</PortalProtectedRouteInner>
    </PortalAuthProvider>
  );
};

export default PortalProtectedRoute;
